import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMinute } from '../../utils/matchClock';
import { matchMetaAPI } from '../../services/api';

const STATUS = {
    prepared: { label: 'Vorbereitung', badge: 'ticker-badge--prep' },
    live: { label: 'LIVE', badge: 'ticker-badge--live' },
    halftime: { label: 'Halbzeit', badge: 'ticker-badge--ht' },
    finished: { label: 'Abgepfiffen', badge: 'ticker-badge--done' },
    report_pending: { label: 'Bericht', badge: 'ticker-badge--prep' },
    published: { label: 'Veröffentlicht', badge: 'ticker-badge--done' },
};

const BROADCAST = {
    off: { label: 'Ticker aus', badge: 'ticker-badge--prep' },
    live: { label: 't12 live', badge: 'ticker-badge--broadcast' },
    closed: { label: 'Beendet', badge: 'ticker-badge--done' },
};

export default function TickerHeader({
    game,
    project,
    round,
    meta,
    displayMinute,
    onClockAction,
    onMetaUpdate,
    onOpenMore,
}) {
    const navigate = useNavigate();
    const menuRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [editingScore, setEditingScore] = useState(false);
    const [scoreInput, setScoreInput] = useState({ home: '', away: '' });
    const [broadcastBusy, setBroadcastBusy] = useState(false);

    const homeTeam = game?.hid?.name || 'Heim';
    const awayTeam = game?.aid?.name || 'Gast';
    const homeScore = meta?.scoreFT?.home ?? game?.scoreh;
    const awayScore = meta?.scoreFT?.away ?? game?.scorea;

    const status = meta?.status || 'prepared';
    const statusInfo = STATUS[status] || STATUS.prepared;
    const isLive = status === 'live';
    const isPaused = meta?.clockPaused;
    const half = meta?.currentHalf || 1;
    const minuteStr = formatMinute(displayMinute, half);
    const showMinute = isLive || status === 'halftime';

    const broadcastStatus = meta?.tickerPublicStatus || 'off';
    const broadcastInfo = BROADCAST[broadcastStatus] || BROADCAST.off;

    useEffect(() => {
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    const handleTickerStart = async () => {
        if (!game?._id || broadcastBusy) return;
        setBroadcastBusy(true);
        try {
            const res = await matchMetaAPI.tickerStart(game._id);
            onMetaUpdate?.(res.data);
        } catch (e) { console.error(e); }
        finally { setBroadcastBusy(false); setMenuOpen(false); }
    };

    const handleTickerStop = async () => {
        if (!game?._id || broadcastBusy) return;
        if (!window.confirm('Ticker auf tivoli12 beenden?')) return;
        setBroadcastBusy(true);
        try {
            const res = await matchMetaAPI.tickerStop(game._id);
            onMetaUpdate?.(res.data);
        } catch (e) { console.error(e); }
        finally { setBroadcastBusy(false); setMenuOpen(false); }
    };

    const openScoreEdit = () => {
        setScoreInput({
            home: homeScore !== null && homeScore !== undefined ? String(homeScore) : '0',
            away: awayScore !== null && awayScore !== undefined ? String(awayScore) : '0',
        });
        setEditingScore(true);
    };

    const saveScore = async () => {
        try {
            const scoreFT = {
                home: parseInt(scoreInput.home, 10) || 0,
                away: parseInt(scoreInput.away, 10) || 0,
            };
            const res = await matchMetaAPI.update(game._id, { scoreFT });
            onMetaUpdate?.(res.data);
        } catch (e) { console.error(e); }
        setEditingScore(false);
    };

    const clockItems = [];
    if (status === 'prepared') clockItems.push({ key: 'kickoff', label: '▶ Anpfiff' });
    if (isLive && !isPaused) {
        clockItems.push({ key: 'pause', label: '⏸ Pause' });
        if (half === 1) clockItems.push({ key: 'halftime', label: 'Halbzeit' });
        if (half === 2) clockItems.push({ key: 'fulltime', label: 'Abpfiff' });
    }
    if (isLive && isPaused) {
        clockItems.push({ key: 'resume', label: '▶ Weiter' });
        clockItems.push({ key: 'halftime', label: 'Halbzeit' });
        clockItems.push({ key: 'fulltime', label: 'Abpfiff' });
    }
    if (status === 'halftime') {
        clockItems.push({ key: 'second_half', label: '▶ 2. HZ' });
        clockItems.push({ key: 'fulltime', label: 'Abpfiff' });
    }

    return (
        <header className="ticker-header ticker-header--compact">
            <div className="ticker-header-row">
                <button
                    type="button"
                    onClick={() => navigate(`/schedule/${project?._id}/rounds/${round?._id}`)}
                    className="ticker-header-back"
                >
                    ← {round?.name || 'Spiele'}
                </button>

                <div className="ticker-header-scoreblock">
                    <span className="ticker-header-team ticker-header-team--home">{homeTeam}</span>

                    <div className="ticker-header-center">
                        {showMinute && (
                            <span className="ticker-header-minute">
                                {minuteStr}{isPaused ? ' ⏸' : ''}
                            </span>
                        )}
                        {editingScore ? (
                            <div className="ticker-header-score-edit">
                                <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    value={scoreInput.home}
                                    onChange={(e) => setScoreInput((s) => ({ ...s, home: e.target.value }))}
                                    className="ticker-score-input"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveScore();
                                        if (e.key === 'Escape') setEditingScore(false);
                                    }}
                                />
                                <span>:</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    value={scoreInput.away}
                                    onChange={(e) => setScoreInput((s) => ({ ...s, away: e.target.value }))}
                                    className="ticker-score-input"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveScore();
                                        if (e.key === 'Escape') setEditingScore(false);
                                    }}
                                />
                                <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={saveScore}>✓</button>
                            </div>
                        ) : (
                            <button type="button" className="ticker-header-score" onClick={openScoreEdit} title="Spielstand bearbeiten">
                                {homeScore ?? '–'}:{awayScore ?? '–'}
                            </button>
                        )}
                        <span className={`ticker-badge ${statusInfo.badge} ${isLive && !isPaused ? 'ticker-live-pulse' : ''}`}>
                            {isLive && !isPaused && <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />}
                            {statusInfo.label}
                        </span>
                    </div>

                    <span className="ticker-header-team ticker-header-team--away">{awayTeam}</span>
                </div>

                <div className="ticker-header-tools" ref={menuRef}>
                    <span className={`ticker-badge ${broadcastInfo.badge}`}>{broadcastInfo.label}</span>
                    <button
                        type="button"
                        className="ticker-header-menu-btn"
                        onClick={() => setMenuOpen((v) => !v)}
                    >
                        ⚙
                    </button>
                    {menuOpen && (
                        <div className="ticker-header-dropdown">
                            {broadcastStatus === 'off' && (
                                <button type="button" onClick={handleTickerStart} disabled={broadcastBusy}>📡 Ticker starten</button>
                            )}
                            {broadcastStatus === 'live' && (
                                <button type="button" onClick={handleTickerStop} disabled={broadcastBusy}>📡 Ticker beenden</button>
                            )}
                            {clockItems.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => { onClockAction(item.key); setMenuOpen(false); }}
                                >
                                    {item.label}
                                </button>
                            ))}
                            <hr />
                            <button type="button" onClick={() => { onOpenMore?.('lineup'); setMenuOpen(false); }}>👥 Aufstellung bearbeiten</button>
                            <button type="button" onClick={() => { onOpenMore?.('report'); setMenuOpen(false); }}>📄 Bericht</button>
                            <button type="button" onClick={() => { onOpenMore?.('embed'); setMenuOpen(false); }}>🌐 Widget</button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
